import { useState } from 'react';
import ServiceNavbar from '../services/shared/ServiceNavbar';
import Footer from '../home/components/Footer';

const serviceOptions = [
  'New Construction Wiring',
  'Residential Service & Repair',
  'Generator Installation',
  'EV Charger Installation',
  'Solar & Panel Upgrade',
  'Specialty Lighting',
  'Other / Not Sure',
];

const hours = [
  { day: 'Monday – Friday', time: '7:00 AM – 5:00 PM' },
  { day: 'Saturday', time: 'By Appointment' },
  { day: 'Sunday', time: 'Closed' },
];

const serviceAreas = [
  'Kalamazoo', 'Portage', 'Battle Creek', 'Paw Paw',
  'Plainwell', 'Otsego', 'Mattawan', 'Vicksburg',
  'Galesburg', 'Augusta', 'Richland', 'Schoolcraft',
];

const FORM_ACTION = 'https://readdy.ai/api/form/d72jvrhsakbatd8b3lr0';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '', phone: '', email: '', service: '', message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const params = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => params.append(k, v));
      await fetch(FORM_ACTION, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-pg font-sans">
      <ServiceNavbar />

      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="relative overflow-hidden pt-36 pb-20">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/3340f948ebeaf2c74bb6cd1b51bc369c.jpg"
            alt="DOC Electric contact"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#060E1C]/95 via-[#0A1628]/88 to-[#060E1C]/70" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#060E1C]/50 via-transparent to-[#0A1628]/90" />
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#00D9FF]/20 to-transparent" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-[1px] w-8 bg-[#00D9FF]" />
            <span className="text-[#00D9FF] text-[11px] font-bold tracking-[3px] uppercase">Contact Us</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-black text-white leading-tight tracking-tight mb-4 max-w-2xl">
            Let&apos;s Talk About<br />
            <span className="text-[#00D9FF]">Your Project.</span>
          </h1>
          <p className="text-white/70 text-lg max-w-xl leading-relaxed mb-8">
            Request a free estimate, ask a question, or describe what you need.
            We respond fast, quote fairly, and show up when we say we will.
          </p>
          {/* Trust chips */}
          <div className="flex flex-wrap gap-3">
            {['Free Estimates', 'Responds Within 1 Business Day', 'Licensed & Insured', 'No Pressure, Ever'].map((chip) => (
              <div key={chip} className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/5 backdrop-blur-sm">
                <span className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-check-line text-[#00D9FF] text-xs" />
                </span>
                <span className="text-white/75 text-[12px] font-medium whitespace-nowrap">{chip}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Main: Form + Info ─────────────────────────────────── */}
      <section className="relative bg-dp py-20 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[1px] bg-gradient-to-r from-transparent via-[#00D9FF]/15 to-transparent" />

        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16 items-start">

            {/* Left: Form (3/5) */}
            <div className="lg:col-span-3 scroll-reveal scroll-from-left">
              <div className="bg-pg border border-[var(--bd)] rounded-2xl p-8 md:p-10">
                {submitted ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div className="w-16 h-16 flex items-center justify-center bg-[#00D9FF]/10 rounded-full border border-[#00D9FF]/30 mb-5">
                      <i className="ri-checkbox-circle-fill text-[#00D9FF] text-3xl" />
                    </div>
                    <h3 className="text-[var(--tp)] font-black text-2xl mb-2">Message Received!</h3>
                    <p className="text-[var(--tm)] text-[15px] leading-relaxed max-w-sm">
                      Thank you for reaching out. We&apos;ll review your request and get back to you within one business day.
                    </p>
                    <a
                      href="/"
                      className="mt-8 px-6 py-3 bg-[#00D9FF] text-[#0A1628] font-bold text-[13px] uppercase tracking-wider rounded-lg hover:bg-[#00c5e8] transition-colors duration-200 cursor-pointer whitespace-nowrap"
                    >
                      Back to Home
                    </a>
                  </div>
                ) : (
                  <>
                    <div className="mb-7">
                      <h2 className="text-[var(--tp)] font-black text-2xl mb-1">Request a Free Estimate</h2>
                      <p className="text-[var(--tm)] text-[14px]">Fill out the form and we&apos;ll be in touch promptly.</p>
                    </div>
                    <form
                      onSubmit={handleSubmit}
                      data-readdy-form
                      id="contact-page-form"
                      className="flex flex-col gap-5"
                    >
                      {/* Name + Phone */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-[var(--tm)] text-[11px] font-bold uppercase tracking-wider mb-1.5">Full Name *</label>
                          <input
                            type="text"
                            name="name"
                            required
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="John Smith"
                            className="w-full bg-[var(--inp-bg)] border border-[var(--inp-border)] focus:border-[#00D9FF]/50 rounded-lg px-4 py-3 text-[var(--inp-text)] text-sm outline-none transition-all duration-200"
                          />
                        </div>
                        <div>
                          <label className="block text-[var(--tm)] text-[11px] font-bold uppercase tracking-wider mb-1.5">Phone *</label>
                          <input
                            type="tel"
                            name="phone"
                            required
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="(269) 555-0100"
                            className="w-full bg-[var(--inp-bg)] border border-[var(--inp-border)] focus:border-[#00D9FF]/50 rounded-lg px-4 py-3 text-[var(--inp-text)] text-sm outline-none transition-all duration-200"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-[var(--tm)] text-[11px] font-bold uppercase tracking-wider mb-1.5">Email Address</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="you@email.com"
                          className="w-full bg-[var(--inp-bg)] border border-[var(--inp-border)] focus:border-[#00D9FF]/50 rounded-lg px-4 py-3 text-[var(--inp-text)] text-sm outline-none transition-all duration-200"
                        />
                      </div>
                      <div>
                        <label className="block text-[var(--tm)] text-[11px] font-bold uppercase tracking-wider mb-1.5">Service Needed *</label>
                        <select
                          name="service"
                          required
                          value={formData.service}
                          onChange={handleChange}
                          className="w-full bg-[var(--inp-bg2)] border border-[var(--inp-border)] focus:border-[#00D9FF]/50 rounded-lg px-4 py-3 text-[var(--inp-text)] text-sm outline-none transition-all duration-200 cursor-pointer"
                        >
                          <option value="">Select a service...</option>
                          {serviceOptions.map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[var(--tm)] text-[11px] font-bold uppercase tracking-wider mb-1.5">Project Description</label>
                        <textarea
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          rows={5}
                          maxLength={500}
                          placeholder="Describe your project..."
                          className="w-full bg-[var(--inp-bg)] border border-[var(--inp-border)] focus:border-[#00D9FF]/50 rounded-lg px-4 py-3 text-[var(--inp-text)] text-sm outline-none transition-all duration-200 resize-none"
                        />
                        <div className="text-right text-[var(--tm)] text-[11px] mt-1">{formData.message.length}/500</div>
                      </div>
                      <button
                        type="submit"
                        disabled={submitting}
                        className="w-full py-4 bg-[#00D9FF] hover:bg-[#00c5e8] disabled:opacity-60 text-[#0A1628] font-black text-[13px] uppercase tracking-wider rounded-lg transition-all duration-300 hover:shadow-[0_0_28px_rgba(0,217,255,0.4)] hover:-translate-y-0.5 cursor-pointer whitespace-nowrap"
                      >
                        {submitting ? 'Sending...' : 'Send My Request'}
                      </button>
                      <p className="text-[var(--tm)] text-[11px] text-center">
                        Licensed &amp; Insured · Kalamazoo, MI · No obligation, no pressure
                      </p>
                    </form>
                  </>
                )}
              </div>
            </div>

            {/* Right: Info (2/5) */}
            <div className="lg:col-span-2 flex flex-col gap-6 scroll-reveal scroll-from-right">
              <div className="bg-pg border border-[var(--bd)] rounded-2xl p-7">
                <h3 className="text-[var(--tp)] font-black text-[16px] mb-5 uppercase tracking-wider">Contact Directly</h3>
                <div className="flex flex-col gap-5">
                  <a href="tel:+12693700765" className="flex items-center gap-4 group cursor-pointer">
                    <div className="w-11 h-11 flex items-center justify-center bg-[#00D9FF]/10 group-hover:bg-[#00D9FF]/20 rounded-xl flex-shrink-0 transition-colors duration-200">
                      <i className="ri-phone-fill text-[#00D9FF] text-lg" />
                    </div>
                    <div>
                      <div className="text-[var(--tm)] text-[11px] font-medium uppercase tracking-wider mb-0.5">Phone / Text</div>
                      <div className="text-[var(--tp)] font-bold text-lg group-hover:text-[#00D9FF] transition-colors duration-200">
                        (269) 370-0765
                      </div>
                    </div>
                  </a>
                  <div className="flex items-center gap-4">
                    <div className="w-11 h-11 flex items-center justify-center bg-[#00D9FF]/10 rounded-xl flex-shrink-0">
                      <i className="ri-map-pin-2-fill text-[#00D9FF] text-lg" />
                    </div>
                    <div>
                      <div className="text-[var(--tm)] text-[11px] font-medium uppercase tracking-wider mb-0.5">Location</div>
                      <div className="text-[var(--tp)] font-semibold text-[15px]">Kalamazoo, MI</div>
                      <div className="text-[var(--ts)] text-[12px]">Serving southwest Michigan</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Business hours */}
              <div className="bg-pg border border-[var(--bd)] rounded-2xl p-7">
                <h3 className="text-[var(--tp)] font-black text-[16px] mb-5 uppercase tracking-wider">Business Hours</h3>
                <div className="flex flex-col gap-3">
                  {hours.map((h) => (
                    <div key={h.day} className="flex items-center justify-between py-2.5 border-b border-[var(--bd)] last:border-0">
                      <span className="text-[var(--tm)] text-[13px] font-medium">{h.day}</span>
                      <span className={`text-[13px] font-bold ${h.time === 'Closed' ? 'text-[var(--td)]' : 'text-[#00D9FF]'}`}>
                        {h.time}
                      </span>
                    </div>
                  ))}
                </div>
                <p className="mt-4 text-[var(--ts)] text-[12px] leading-relaxed">
                  Emergency electrical issues? Call anytime — we&apos;ll do our best to help.
                </p>
              </div>

              {/* Map */}
              <div className="rounded-2xl overflow-hidden border border-[#1E3048]" style={{ height: '220px' }}>
                <iframe
                  title="DOC Electric Kalamazoo location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d94874.77583963684!2d-85.6906!3d42.2917!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88179fc37e3e93e7%3A0x43ed2b48a81b08d1!2sKalamazoo%2C%20MI!5e0!3m2!1sen!2sus!4v1700000000000"
                  width="100%"
                  height="220"
                  style={{ border: 0, filter: 'invert(90%) hue-rotate(180deg)' }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* ── Service Area Strip ───────────────────────────────── */}
      <section className="relative bg-pg border-t border-[var(--bd)] py-14 overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#00D9FF]/10 to-transparent" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 sm:gap-10">
            <div className="flex-shrink-0">
              <div className="text-[var(--tm)] text-[11px] font-bold uppercase tracking-[3px] mb-1">Service Area</div>
              <div className="text-[var(--tp)] font-black text-[17px]">Southwest Michigan</div>
            </div>
            <div className="w-px h-10 bg-[var(--bd)] hidden sm:block flex-shrink-0" />
            <div className="flex flex-wrap gap-2">
              {serviceAreas.map((area) => (
                <span
                  key={area}
                  className="px-3 py-1.5 bg-[var(--dp)] border border-[var(--bd)] rounded-full text-[var(--ts)] text-[12px] font-medium"
                >
                  {area}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
